/**
 * Salary formula readability helper (Iter 111.1 · Obs 14).
 *
 * The payroll engine uses stable component **codes** (e.g. `BASIC`, `HRA`,
 * `GROSS`) inside formulas because the codes are the immutable identity
 * used at compute time. Codes are audit-safe: renaming a component name
 * never risks breaking downstream computation.
 *
 * For UI display, however, raw codes look opaque to a business operator:
 *
 *   "0.40 * BASIC"
 *   "max(GROSS - BASIC - 0.40 * BASIC - 1600 - 1250, 0)"
 *
 * `prettyFormula` returns a human-readable variant that replaces every
 * known code TOKEN with the resolved display name while leaving stable
 * codes intact when a name mapping is unknown:
 *
 *   "0.40 × Basic Pay"
 *   "max(Gross Salary − Basic Pay − 0.40 × Basic Pay − 1600 − 1250, 0)"
 *
 * Reserved system identifiers (GROSS · CTC_ANNUAL · WORKING_DAYS ·
 * PAID_DAYS · LOP_DAYS · MONTH_DAYS) are labelled from the built-in map
 * even when they are not returned by the component list.
 */

// System-reserved identifiers surfaced by the payroll engine's evaluator.
const SYSTEM_TOKENS = {
    GROSS: "Gross Salary",
    CTC_ANNUAL: "Annual CTC",
    WORKING_DAYS: "Working Days",
    PAID_DAYS: "Paid Days",
    LOP_DAYS: "LoP Days",
    MONTH_DAYS: "Month Days",
    ATTENDANCE_FACTOR: "Attendance Factor",
    // Compliance-derived identifiers (payroll_engine references).
    EMPLOYEE_STATE: "Employee State",
    STATE: "State",
};

// Tokens that MUST NOT be replaced — Python-ish keywords the DSL supports.
const RESERVED_KEYWORDS = new Set([
    "max", "min", "round", "abs", "floor", "ceil",
    "if", "else", "and", "or", "not", "True", "False",
]);

/**
 * Build a name lookup from a list of Salary Components as returned by
 * `/api/payroll/component/list`.
 */
export function buildComponentNameMap(components = []) {
    const map = { ...SYSTEM_TOKENS };
    for (const c of components) {
        if (c && c.code) {
            // Only override when we have a real, non-empty name.
            if (c.name && String(c.name).trim()) {
                map[c.code] = c.name;
            }
        }
    }
    return map;
}

/**
 * Return a readable rendition of `formula` using the supplied
 * codeToName map (from `buildComponentNameMap`). Unknown identifiers
 * are preserved verbatim so the operator still knows a token exists.
 *
 * Note: purely a display transformation. NEVER pass the result to the
 * backend — the engine consumes the raw formula stored in the doc.
 */
export function prettyFormula(formula, codeToName) {
    if (!formula || typeof formula !== "string") return formula || "";
    const map = codeToName || SYSTEM_TOKENS;
    // Match identifiers: letters/digits/underscore starting with letter or underscore.
    return formula.replace(/[A-Za-z_][A-Za-z0-9_]*/g, (tok) => {
        if (RESERVED_KEYWORDS.has(tok)) return tok;
        const name = map[tok];
        return name || tok;
    })
    // Cosmetic tweaks that only run after the identifier pass so we
    // never munge inside an identifier accidentally.
    .replace(/\s\*\s/g, " × ")
    .replace(/\s-\s/g, " − ");
}

/**
 * Return a compact "explain" string suitable for a tooltip:
 *
 *   Raw  : 0.40 * BASIC
 *   Read : 0.40 × Basic Pay
 *
 * Both forms shown so the operator can trust the underlying stable code
 * without losing the readable view.
 */
export function formulaTooltip(formula, codeToName) {
    if (!formula) return "";
    const pretty = prettyFormula(formula, codeToName);
    if (pretty === formula) return `Formula: ${formula}`;
    return `Readable: ${pretty}\nRaw: ${formula}`;
}
